import React, { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../config/firebase";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  getUserProfile,
  createUserProfile,
  ROLES,
  hasRole,
  getInvitationByEmail,
  acceptInvitation,
} from "../services/users";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadUserProfile = async (firebaseUser) => {
    try {
      let userProfile = await getUserProfile(firebaseUser.uid);

      if (!userProfile) {
        // First-time login - check for invitation or assign default role
        let role = ROLES.USER;
        const isSuperAdmin = firebaseUser.email === "paul@caladyn.com";

        if (isSuperAdmin) {
          role = ROLES.SUPER_ADMIN;
        } else {
          // Check for pending invitation
          const invitation = await getInvitationByEmail(firebaseUser.email);
          if (invitation) {
            role = invitation.role;
            await acceptInvitation(firebaseUser.email);
          }
        }

        userProfile = await createUserProfile(firebaseUser.uid, {
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          role,
        });
      }

      setProfile(userProfile);
    } catch (err) {
      console.error("Error fetching user profile:", err);
      setError("Failed to load user profile");
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await loadUserProfile(firebaseUser);
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const login = async (email, password) => {
    setError(null);
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  };

  const register = async (email, password) => {
    setError(null);
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result.user;
  };

  const logout = async () => {
    await signOut(auth);
  };

  // Refresh profile (useful after role changes)
  const refreshProfile = async () => {
    if (user) {
      await loadUserProfile(user);
    }
  };

  // Role checking helper
  const checkRole = (requiredRole) => {
    if (!profile) return false;
    return hasRole(profile.role, requiredRole);
  };

  const value = {
    user,
    profile,
    loading,
    error,
    login,
    register,
    logout,
    refreshProfile,
    checkRole,
    isAdmin: profile ? hasRole(profile.role, ROLES.ADMIN) : false,
    isSuperAdmin: profile?.role === ROLES.SUPER_ADMIN,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
